import React from 'react';
import { FaWhatsapp, FaInbox } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import { HiOutlinePencilSquare, HiOutlineTrash, HiOutlinePlus, HiOutlineTag, HiOutlineXMark, HiOutlineCursorArrowRays, HiOutlineClipboard } from 'react-icons/hi2';
import { Provider, ProviderCategories } from '../../types';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { deleteProvidersSelectedByIds } from '../../../../../../crud/management_api/providers';
import { CategoriesOption } from './components/utils/functions';
import CreateProvider from './components/CreateProvider/CreateProvider';
import DeleteProvider from './components/DeleteProvider/DeleteProvider';
import UpdateProvider from './components/UpdateProvider/UpdateProvider';
import UnassignCategory from './components/UnassignCategory/UnassignCategory';
import AssignCategory from './components/AssignCategory/AssignCategory';
import ProviderCategoryManager from './components/ProviderCategoryManager/ProviderCategoryManager';
import TableSearchBar from '../../../../../../components/common/Table/TableSearchBar';
import FilterDropdown from '../../../Stock/components/ResponsiveFilters/components/FilterDropdown/FilterDropdown';

interface TableProps {
  providers: Provider[];
  categories: CategoriesOption[];
  providerCategories: ProviderCategories[];
  setProviders: (p: Provider[]) => void;
  selectedCategorie: any[];
  setSelectedCategorie: (cats: any[]) => void;
  CategorieOptions: { value: number; label: string }[];
  onCategoriesChanged: () => void;
}

const Table: React.FC<TableProps> = ({ providers, categories, providerCategories, setProviders, selectedCategorie, setSelectedCategorie, CategorieOptions, onCategoriesChanged }) => {
  const [currentData, setCurrentData] = React.useState<Provider[]>(providers);
  const [filteredData, setFilteredData] = React.useState<Provider[]>(providers);

  useEffect(() => {
    setCurrentData(providers);
    setFilteredData(providers);
  }, [providers]);

  const [showSelection, setShowSelection] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUnassignCategoryModal, setShowUnassignCategoryModal] = useState(false);
  const [showAssignCategoryModal, setShowAssignCategoryModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [currentProvider, setCurrentProvider] = useState<Provider | null>(null);
  const [currentCategory, setCurrentCategory] = useState<ProviderCategories | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (!showSelection) setSelectedIds([]);
  }, [showSelection]);

  const toggleSelect = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      return toast.error('Selecciona al menos un proveedor');
    }
    try {
      await deleteProvidersSelectedByIds(selectedIds);
      toast.success(`Eliminados ${selectedIds.length} proveedores`);
      const updated = currentData.filter(p => !selectedIds.includes(p.provider_id as any));
      setCurrentData(updated);
      setProviders(updated);
      setShowSelection(false);
    } catch {
      toast.error('Error al eliminar proveedores seleccionados');
    }
  };

  const handleShowDeleteModal = (provider: Provider) => {
    setCurrentProvider(provider);
    setShowDeleteModal(true);
  };

  const handleShowEditModal = async (provider: Provider) => {
    if (provider) {
      setCurrentProvider(provider);
      setShowEditModal(true);
    } else {
      console.error('Failed to fetch user for editing.');
      toast.error('El usuario no puede ser editado');
    }
  };

  const handleShowUnassignCategoryModal = (provider: Provider, category: ProviderCategories) => {
    setCurrentProvider(provider);
    setCurrentCategory(category);
    setShowUnassignCategoryModal(true);
  };

  const handleShowAssignCategoryModal = (provider: Provider) => {
    setCurrentProvider(provider);
    setShowAssignCategoryModal(true);
  };

  const handleCategoryOperationSuccess = (updatedProvider: Provider) => {
    const updateProviderInList = (list: Provider[]) =>
      list.map(p => p.provider_id === updatedProvider.provider_id ? updatedProvider : p);

    setCurrentData(updateProviderInList(currentData));
    setFilteredData(updateProviderInList(filteredData));
    setProviders(updateProviderInList(providers));
  };

  const handleCopyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${type} copiado al portapapeles`);
    }).catch(() => {
      toast.error('Error al copiar al portapapeles');
    });
  };

  const handleOpenWhatsApp = (number: string) => {
    window.open(`https://wa.me/${number}`, '_blank');
  };

  const renderFilterBar = () => (
    <div className="mp-toolbar flex flex-wrap items-center gap-3">
      <div className="min-w-[220px] max-w-[340px] flex-1">
        <TableSearchBar
          data={currentData}
          updateFilteredData={setFilteredData}
          searchFields={['name', 'email', 'number']}
          placeholder="Buscar proveedores..."
        />
      </div>

      <div className="min-w-[140px] max-w-[200px] flex-shrink-0">
        <FilterDropdown
          options={CategorieOptions}
          selectedItems={selectedCategorie}
          onChange={setSelectedCategorie}
          placeholder="Categoría"
        />
      </div>

      <div className="flex items-center gap-2 ml-auto">
        <button className="mp-btn-secondary" onClick={() => setShowCategoryManager(true)}>
          <HiOutlineTag size={15} /> Categorías
        </button>
        {!showSelection && (
          <button className="mp-btn-primary" onClick={() => setShowCreateModal(true)}>
            <HiOutlinePlus size={15} /> Crear proveedor
          </button>
        )}
        <button className="mp-btn-secondary" onClick={() => setShowSelection(!showSelection)}>
          {showSelection ? <><HiOutlineXMark size={15} /> Cancelar</> : <><HiOutlineCursorArrowRays size={15} /> Seleccionar</>}
        </button>
        {showSelection && selectedIds.length > 0 && (
          <button
            className="mp-btn-primary"
            style={{ background: 'linear-gradient(135deg, #dc3545, #b02a37)' }}
            onClick={handleBulkDelete}
          >
            <HiOutlineTrash size={15} /> Eliminar ({selectedIds.length})
          </button>
        )}
      </div>
    </div>
  );

  const renderTable = () => (
    <div className="mp-table-card" style={{ flex: 1, overflow: 'auto' }}>
      <table className="mp-table">
        <thead className="mp-table-head">
          <tr>
            {showSelection && <th style={{ width: 40 }}></th>}
            <th>Nombre</th>
            <th>Número</th>
            <th className="no-responsive">Email</th>
            <th>Categoría</th>
            <th style={{ width: 120, textAlign: 'center' }}>Acciones</th>
          </tr>
        </thead>
        <tbody className="mp-table-body">
          {filteredData.map(provider => (
            <tr key={provider.provider_id}>
              {showSelection && (
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(Number(provider.provider_id))}
                    onChange={() => toggleSelect(Number(provider.provider_id))}
                    className="w-4 h-4 rounded border-mp-border text-mp-orange focus:ring-mp-orange/20 cursor-pointer"
                  />
                </td>
              )}
              <td className="font-medium">{provider.name}</td>
              <td>
                {provider.number?.trim() ? (
                  <div className="flex items-center gap-1.5">
                    <span>{provider.number}</span>
                    <button
                      className="mp-btn-icon"
                      style={{ width: 24, height: 24 }}
                      onClick={() => handleCopyToClipboard(provider.number!, 'Número')}
                      title="Copiar"
                    >
                      <HiOutlineClipboard size={14} />
                    </button>
                  </div>
                ) : (
                  <span style={{ opacity: 0.4 }}>—</span>
                )}
              </td>
              <td className="no-responsive">
                {provider.email?.trim() ? (
                  <div className="flex items-center gap-1.5">
                    <span>{provider.email}</span>
                    <button
                      className="mp-btn-icon"
                      style={{ width: 24, height: 24 }}
                      onClick={() => handleCopyToClipboard(provider.email!, 'Email')}
                      title="Copiar"
                    >
                      <HiOutlineClipboard size={14} />
                    </button>
                  </div>
                ) : (
                  <span style={{ opacity: 0.4 }}>—</span>
                )}
              </td>
              <td>
                <div className="provider-categories-cell">
                  <div className="categories-list">
                    {provider.categories && provider.categories.length > 0 && (
                      provider.categories.map((category) => (
                        <div key={category.category_id} className="category-badge">
                          <span className="category-name">{category.name}</span>
                          <button
                            className="category-remove-btn"
                            onClick={() => handleShowUnassignCategoryModal(provider, category)}
                            title="Desasignar categoría"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <button
                    className="category-add-btn"
                    onClick={() => handleShowAssignCategoryModal(provider)}
                    title="Asignar categoría"
                  >
                    <HiOutlinePlus size={12} />
                  </button>
                </div>
              </td>
              <td>
                <div className="flex items-center justify-center gap-1.5">
                  {provider.number?.trim() && (
                    <button className="mp-btn-icon" onClick={() => handleOpenWhatsApp(provider.number!)} title="WhatsApp">
                      <FaWhatsapp size={16} />
                    </button>
                  )}
                  <button className="mp-btn-icon" onClick={() => handleShowEditModal(provider)} title="Editar">
                    <HiOutlinePencilSquare size={16} />
                  </button>
                  <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleShowDeleteModal(provider)} title="Eliminar">
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderNoData = () => (
    <div className="mp-table-card" style={{ padding: '3rem', textAlign: 'center' }}>
      <FaInbox size={32} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
      <div style={{ opacity: 0.6, marginBottom: '1rem' }}>No has creado ningún proveedor aún!</div>
      <button className="mp-btn-primary" onClick={() => setShowCreateModal(true)}>
        <HiOutlinePlus size={15} /> Crear Proveedor
      </button>
    </div>
  );

  return (
    <>
      {renderFilterBar()}

      {filteredData.length === 0 ? renderNoData() : renderTable()}

      <DeleteProvider showDeleteModal={showDeleteModal} setShowDeleteModal={setShowDeleteModal} currentProvider={currentProvider} currentData={currentData} setCurrentData={setCurrentData} />

      <CreateProvider showCreateModal={showCreateModal} setShowCreateModal={setShowCreateModal} currentData={currentData} setCurrentData={setCurrentData} />

      <UpdateProvider showEditModal={showEditModal} setShowEditModal={setShowEditModal} setCurrentData={setCurrentData} currentData={currentData} currentProvider={currentProvider} />

      {currentProvider && currentCategory && (
        <UnassignCategory
          showModal={showUnassignCategoryModal}
          setShowModal={setShowUnassignCategoryModal}
          provider={currentProvider}
          category={currentCategory}
          onSuccess={handleCategoryOperationSuccess}
        />
      )}

      {currentProvider && (
        <AssignCategory
          showModal={showAssignCategoryModal}
          setShowModal={setShowAssignCategoryModal}
          provider={currentProvider}
          onSuccess={handleCategoryOperationSuccess}
        />
      )}

      <ProviderCategoryManager
        show={showCategoryManager}
        setShow={setShowCategoryManager}
        categories={providerCategories}
        onChanged={onCategoriesChanged}
      />
    </>
  );
};

export default Table;